import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, FileAddOutlined, SearchOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import api from '../utils/axios';
import { handleApiError } from '../utils/errorHandler';
import ClientImport from './ClientImport';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Client {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Metrics {
  totalClients: number;
  individualClients: number;
  companyClients: number;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const ClientManagement: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalClients: 0,
    individualClients: 0,
    companyClients: 0
  });
  const [showImportModal, setShowImportModal] = useState(false);

  const debouncedSearchText = useDebounce(searchText, 400);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Client[]; pagination: any }>('/clients', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: debouncedSearchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD')
        }
      });
      if (response.data.success) {
        setClients(response.data.data);
        // Update pagination from response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.data.pagination.page,
            total: response.data.pagination.total,
            pageSize: response.data.pagination.limit
          }));
        }
        calculateMetrics(response.data.data);
      } else {
        message.error('Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      message.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, debouncedSearchText, dateRange]);

  const calculateMetrics = (data: Client[]) => {
    setMetrics({
      totalClients: data.length,
      individualClients: data.filter(client => client.type === 'INDIVIDUAL').length,
      companyClients: data.filter(client => client.type === 'COMPANY').length
    });
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Reset pagination when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [debouncedSearchText, dateRange]);

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setIsModalVisible(true);
    form.setFieldsValue(client);
  };

  const handleDelete = async (id: string) => {
    try {
              await api.delete(`/clients/${id}`);
      message.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      message.error('Failed to delete client');
    }
  };

  const handleCreate = () => {
    setEditingClient(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, values);
        message.success('Client updated successfully');
      } else {
                  await api.post('/clients', values);
        message.success('Client created successfully');
      }
      setIsModalVisible(false);
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      message.error('Failed to save client');
    }
  };

  const columns: ColumnsType<Client> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/clients/${record.id}`)}
          style={{ padding: 0, height: 'auto', textDecoration: 'underline' }}
        >
          {record.type === 'COMPANY' 
            ? record.companyName 
            : `${record.firstName} ${record.lastName}`}
        </Button>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'COMPANY' ? 'blue' : 'green'}>
          {type}
        </Tag>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'success' : 'error'}>
          {status}
        </Tag>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/clients/${record.id}`)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<Client> = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(pagination);
    }

    if (sorter) {
      const { field, order } = sorter as { field: string; order: 'ascend' | 'descend' | null };
      const sortedData = [...clients].sort((a, b) => {
        let aValue: any = a[field as keyof Client];
        let bValue: any = b[field as keyof Client];

        // Handle name field which combines firstName/lastName or companyName
        if (field === 'name') {
          aValue = a.type === 'COMPANY' ? a.companyName : `${a.firstName} ${a.lastName}`;
          bValue = b.type === 'COMPANY' ? b.companyName : `${b.firstName} ${b.lastName}`;
        }

        if (order === 'ascend') {
          return String(aValue).localeCompare(String(bValue));
        }
        if (order === 'descend') {
          return String(bValue).localeCompare(String(aValue));
        }
        return 0;
      });
      setClients(sortedData);
    }
  };

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Clients"
              value={metrics.totalClients}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Individual Clients"
              value={metrics.individualClients}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Company Clients"
              value={metrics.companyClients}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Clients</Title>
          <Space>
            <Input
              placeholder="Search clients..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Space style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Add Client
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />} 
                onClick={() => setShowImportModal(true)}
              >
                Import Clients
              </Button>
            </Space>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={clients}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
        />
      </Card>

      <Modal
        title={editingClient ? 'Edit Client' : 'New Client'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select client type' }]}
          >
            <Select>
              <Option value="INDIVIDUAL">Individual</Option>
              <Option value="COMPANY">Company</Option>
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              return type === 'COMPANY' ? (
                <Form.Item
                  name="companyName"
                  label="Company Name"
                  rules={[{ required: true, message: 'Please enter company name' }]}
                >
                  <Input />
                </Form.Item>
              ) : (
                <>
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <Input />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <ClientImport
        visible={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          fetchClients();
        }}
      />
    </div>
  );
};

export default ClientManagement; 